import React from 'react';
import ReactDOM from 'react-dom';
import { isNil, isEmpty } from 'lodash';
import { ICellEditorParams } from '@ag-grid-community/core';

import { InputSuggest } from 'src/common-ui/index';
import type { Suggestion } from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';

import { DataApi, isListDataApi } from 'configuration-schemas/confdefnView';
import { getValidValues } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.client';
import { getUrl } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import {
  getSelectedValidValue,
  getSelectedValidValues,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Renderers/RenderUtils';
import { ConfigurableGridAggCellEditorState } from 'src/components/ConfigurableGrid/ConfigurableGrid.types';
import Service from 'src/ServiceContainer';
import { AppType } from 'configuration-schemas/general';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { SaveConfirmationModal } from 'src/components/Modals/SaveConfirmationModal';
import { BasicItem } from 'src/types/Scope';
import { style } from 'typestyle';

const overrides = {
  autocomplete: {
    root: style({
      $debugName: 'valid-values-editor-root-overrides',
      height: 'var(--s5-headitor-row-height)',
    }),
    inputRoot: style({
      $debugName: 'valid-values-editor-inputroot-overrides',
      height: 'var(--s5-headitor-row-height)',
      padding: '0 !important',
    }),
    input: style({
      $debugName: 'valid-values-editor-input-overrides',
    }),
  },
};

export interface ValidValuesEditorProps extends ICellEditorParams {
  dataConfig: DataApi;
  multiSelect?: boolean;
  asCsv?: boolean;
  dataQa?: string;
  options?: string[];
  allowEmptyOption?: boolean;
  ignoreCache?: boolean;
  returnSelectionObject: boolean;
  concatOptionValues?: boolean;
  includeCurrent?: boolean;
  clientHandler?: (currentValue: string) => Promise<Suggestion[]>;
  defaultOpenOnFocus?: boolean;
  confirmOnSave?: boolean;
  confirmText?: string;
  // when true, renders a "New" option that lets the user free-text a brand new entry
  allowAddNew?: boolean;
  addNewTitle?: string; // heading for the new-entry modal
  addNewText?: string; // description text for the new-entry modal
}

export interface ValidValuesEditorState extends ConfigurableGridAggCellEditorState {
  validValues: Suggestion[];
  loaded: boolean;
  selectedOption: Suggestion | undefined;
  selectedOptions: Suggestion[];
  useLabelAsValue: boolean; // tells getValue method property to return for actual value
  showConfirm: boolean;
  pendingSelection: Suggestion | undefined;
  showAddNew: boolean;
}
export const addNewOption: Suggestion = { label: 'New', value: '$addNew' };

export default class ValidValuesEditor extends React.Component<ValidValuesEditorProps, ValidValuesEditorState> {
  state: ValidValuesEditorState;
  inputRef!: InputSuggest | null;

  constructor(props: ValidValuesEditorProps) {
    super(props);
    this.state = {
      validValues: [],
      loaded: false,
      selectedOption: undefined,
      selectedOptions: [],
      useLabelAsValue: false,
      valueChosen: false,
      showConfirm: false,
      pendingSelection: undefined,
      showAddNew: false,
    };
  }

  componentDidMount() {
    const {
      dataConfig,
      multiSelect = false,
      options,
      clientHandler,
      value,
      concatOptionValues,
      ignoreCache,
      includeCurrent,
    } = this.props;

    if (!isNil(options)) {
      // validValuesNoServer editor type
      this.setupServerlessSelection();
      this.focus();
      return;
    } else if (!isNil(clientHandler)) {
      clientHandler(value).then((validValues: Suggestion[]) => {
        this.setupSelection(validValues);
      });
      return;
    }

    if (isListDataApi(dataConfig)) {
      Service.pivotService
        .listData(dataConfig.defnId, AppType.Assortment, {
          ...dataConfig.params,
          nestData: false,
        })
        .then((resp) => {
          // In this mode, we currently don't do anything to reshape the response,
          // but instead rely on the pivot returning an array of `value: string, label: string` objects
          const validValues: Suggestion[] = resp.tree as unknown as Suggestion[];
          if (includeCurrent) {
            validValues.unshift({
              // @ts-ignore
              value: this.getValue(),
              // @ts-ignore
              label: this.getValue(),
            });
          }
          // @ts-ignore
          this.setupSelection(validValues);
        });
    } else {
      const url = getUrl(dataConfig);
      const allowEmptyOption = this.props.multiSelect ? false : this.props.allowEmptyOption;
      getValidValues(url, allowEmptyOption, concatOptionValues, ignoreCache)
        .then((validValues) => {
          if (includeCurrent) {
            validValues.unshift({
              value: this.getValue(),
              label: this.getValue(),
            });
          }
          this.setupSelection(validValues);

          if (!multiSelect) {
            this.focus();
          }
        })
        .catch((err) => window.console.log(err));
    }
  }

  setupSelection(validValues: Suggestion[]) {
    const { value, multiSelect = false, asCsv } = this.props;
    const commonState = {
      validValues,
      loaded: true,
    };

    if (!multiSelect) {
      let useLabelAsValue = false;
      let selectedOption = !isNil(value) ? getSelectedValidValue(value, validValues) : undefined;

      if (isNil(selectedOption)) {
        selectedOption = getSelectedValidValue(value, validValues, true);
        useLabelAsValue = !isNil(selectedOption);
      }

      this.setState({
        ...commonState,
        selectedOption,
        useLabelAsValue,
      });
    } else {
      const selectedOptions = !isNil(value) ? getSelectedValidValues(value, validValues, asCsv) : [];

      this.setState({
        ...commonState,
        selectedOptions,
      });
    }
  }

  setupServerlessSelection() {
    const { options } = this.props;
    if (options) {
      const validValues = options.map((selection) => {
        return {
          label: selection,
          value: selection,
        };
      });

      this.setupSelection(validValues);
    }
  }

  focus() {
    setTimeout(() => {
      if (this.inputRef == null) {
        return;
      }
      // eslint-disable-next-line react/no-find-dom-node
      const container = ReactDOM.findDOMNode(this.inputRef);
      if (container) {
        // @ts-ignore
        container.querySelector('input[type="text"]').focus();
      }
    });
  }

  isPopup() {
    return true;
  }

  getValue() {
    const { value, multiSelect = false, returnSelectionObject } = this.props;
    const { loaded, selectedOption, selectedOptions, useLabelAsValue } = this.state;

    if (!loaded) {
      return value;
    }

    // single select validValues
    if (!multiSelect) {
      if (isNil(selectedOption)) {
        return value;
      } else if (!isNil(returnSelectionObject) && returnSelectionObject) {
        return selectedOption;
      }

      return !useLabelAsValue ? selectedOption.value : selectedOption.label;
    }

    // normal multiSelect validValues
    return !isEmpty(selectedOptions)
      ? selectedOptions.map((option: Suggestion) => (!useLabelAsValue ? option.value : option.label))
      : [];
  }

  isCancelAfterEnd() {
    return !this.state.valueChosen;
  }

  onSelect = (selection: Suggestion) => {
    if (selection.value === addNewOption.value) {
      this.setState({ showAddNew: true });
      return;
    }

    this.setState({ selectedOption: selection });
    this.commitSelection(selection);
  };

  // Commits a chosen value, respecting confirmOnSave. Shared by normal selection and new-entry flows.
  commitSelection = (selection: Suggestion) => {
    // When confirmOnSave is configured, defer committing the edit until the user
    // clicks "Apply" in the ConfirmationModal rather than committing on select.
    if (this.props.confirmOnSave) {
      this.setState({
        pendingSelection: selection,
        showConfirm: true,
      });
      return;
    }

    this.setState(
      {
        selectedOption: selection,
        valueChosen: true,
      },
      this.props.stopEditing,
    );
  };

  onAddNewSave = ({ name }: BasicItem) => {
    // Route the free-text entry through the normal commit path so confirmOnSave still applies.
    this.setState(
      {
        showAddNew: false,
      },
      () => this.commitSelection({ label: name, value: name }),
    );
  };

  onAddNewClose = () => {
    // Cancelling the new-entry modal cancels the edit; valueChosen stays false so nothing is applied.
    this.setState({ showAddNew: false }, () => this.props.stopEditing(true));
  };

  onConfirmApply = () => {
    this.setState(
      {
        selectedOption: this.state.pendingSelection,
        valueChosen: true,
        showConfirm: false,
      },
      this.props.stopEditing,
    );
  };

  onConfirmCancel = () => {
    // valueChosen stays false, so isCancelAfterEnd() cancels the edit without applying it.
    this.setState({ showConfirm: false, pendingSelection: undefined }, () => this.props.stopEditing(true));
  };

  onMultiSelect = (selections: Suggestion[]) => {
    this.setState({
      selectedOptions: selections,
    });
  };

  onSubmit = () => {
    if (this.props.multiSelect === true) {
      this.setState({ valueChosen: true }, this.props.stopEditing);
      return;
    }

    if (this.state.selectedOption) {
      this.commitSelection(this.state.selectedOption);
    }
  };

  onCancel = () => this.props.stopEditing(true);

  render() {
    const { multiSelect = false, dataQa, allowAddNew, addNewTitle, addNewText } = this.props;
    const { validValues, selectedOption, selectedOptions, showConfirm, showAddNew, loaded } = this.state;

    // determine selection(s) and handler based on single or multi select
    const onSelectHandler = multiSelect ? undefined : this.onSelect;
    const onMultiSelectHandler = multiSelect ? this.onMultiSelect : undefined;
    const selected = multiSelect ? selectedOptions : selectedOption;
    const addNewOption: Suggestion = { label: this.props.addNewTitle ?? 'New', value: '$addNew' };
    let options: Suggestion[] = [];
    if (!loaded) {
      options = [];
    } else if (allowAddNew && !multiSelect) {
      options = [addNewOption].concat(validValues);
    } else {
      options = validValues;
    }
    return (
      <>
        <InputSuggest
          classes={overrides}
          ref={(ref) => (this.inputRef = ref)}
          validValues={options}
          multiSelect={multiSelect}
          selected={selected}
          onSelect={onSelectHandler}
          onMultiSelect={onMultiSelectHandler}
          open={!showConfirm && !showAddNew}
          dataQa={dataQa}
          defaultOpenOnFocus={true}
          allowPortal={true}
          isLoading={!loaded}
          onCancel={this.onCancel}
          onSubmit={this.onSubmit}
        />
        {showAddNew && (
          <SaveConfirmationModal
            isOpen={true}
            onSave={this.onAddNewSave}
            onClose={this.onAddNewClose}
            nameValidator={(name) => name.trim().length > 0}
            isSaving={false}
            titleText={addNewTitle ?? 'Add New'}
            descriptionText={addNewText ?? 'New value:'}
          />
        )}
        {showConfirm && (
          <ConfirmationModal
            isOpen={true}
            descriptionText={this.props.confirmText ?? 'Apply these changes?'}
            onConfirm={this.onConfirmApply}
            onCancel={this.onConfirmCancel}
          />
        )}
      </>
    );
  }
}
